import { nullableArray, nullableOptional, toSearchParams } from '@shigo-ui/api-client';
import { DashboardTableStatusTone } from '@shigo-ui/dashboard-ui';
import { z } from 'zod';

import { dashboardT } from '../../../lib/messages';

export enum SupplyInventoryAppStatus {
  Active = 'ACTIVE',
  Archived = 'ARCHIVED',
}

export enum SupplyAppsSortProperty {
  Name = 'name',
  AppStoreId = 'appStoreId',
  BundleId = 'bundleId',
  Status = 'status',
  CreatedOn = 'createdOn',
  UpdatedOn = 'updatedOn',
}

export enum SupplyAppsSortDirection {
  Ascending = 'ASC',
  Descending = 'DESC',
}

export enum AppRequestSortProperty {
  AdRequests = 'adRequests',
  BundleId = 'bundleId',
  SuggestedAppName = 'suggestedAppName',
  ConnectedAppName = 'connectedAppName',
  PublisherName = 'publisherName',
  PlatformName = 'platformName',
  CreatedOn = 'createdOn',
}

export enum AppRequestBooleanFilter {
  All = 'ALL',
  Yes = 'TRUE',
  No = 'FALSE',
}

export enum AppRequestDatePreset {
  Today = 'TODAY',
  Last7Days = 'LAST_7_DAYS',
}

const identifierSchema = z.union([z.string(), z.number()]).transform(String);
const referenceContractSchema = z.looseObject({
  id: nullableOptional(identifierSchema),
  name: nullableOptional(z.string()),
  title: nullableOptional(z.string()),
  code: nullableOptional(z.string()),
});

export const supplyAppsReferenceSchema = z
  .union([identifierSchema, referenceContractSchema])
  .transform((reference) => {
    if (typeof reference === 'string') return { id: reference, name: reference };
    const id = reference.id ?? reference.code ?? reference.name ?? reference.title ?? '';
    return {
      id,
      name: reference.name ?? reference.title ?? reference.code ?? reference.id ?? '',
    };
  });

export const supplyAppsReferenceListSchema = z
  .union([
    z.array(supplyAppsReferenceSchema),
    z.looseObject({ content: nullableArray(supplyAppsReferenceSchema) }),
  ])
  .transform((value) => (Array.isArray(value) ? value : value.content));

const nullableString = nullableOptional(z.string());
const nullableBoolean = nullableOptional(z.boolean());

const publisherNamesSchema = z
  .union([z.string(), z.array(z.string())])
  .nullish()
  .transform((value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    return value
      ? value
          .split(',')
          .map((name) => name.trim())
          .filter(Boolean)
      : [];
  });

const appStatusSchema = z.preprocess(
  (value) => (value === null || value === undefined ? SupplyInventoryAppStatus.Active : value),
  z.enum(SupplyInventoryAppStatus),
);

const supplyInventoryAppContractSchema = z.looseObject({
  id: identifierSchema,
  numericId: nullableString,
  name: nullableString,
  status: appStatusSchema,
  appStoreId: nullableString,
  appStoreUrl: nullableString,
  bundleId: nullableString,
  bundleUrl: nullableString,
  domain: nullableString,
  description: nullableString,
  adsTxtLocation: nullableString,
  language: nullableString,
  ssai: nullableBoolean,
  createdOn: nullableString,
  updatedOn: nullableString,
  createdBy: z.unknown().nullish(),
  updatedBy: nullableString,
  publisherIds: nullableArray(identifierSchema),
  publisherNames: publisherNamesSchema,
  iabCategories: nullableArray(supplyAppsReferenceSchema),
  platform: supplyAppsReferenceSchema.nullish(),
  network: supplyAppsReferenceSchema.nullish(),
});

export const supplyInventoryAppSchema = supplyInventoryAppContractSchema.transform((app) => ({
  ...app,
  name: app.name ?? app.bundleId ?? app.appStoreId ?? app.id,
  platform: app.platform ?? undefined,
  network: app.network ?? undefined,
}));

export const supplyInventoryAppEditSchema = z
  .looseObject({})
  .transform((sourceDto) => ({ sourceDto, app: sourceDto }))
  .pipe(z.object({ sourceDto: z.looseObject({}), app: supplyInventoryAppSchema }))
  .transform(({ sourceDto, app }) => ({
    ...app,
    sourceDto,
  }));

export const supplyInventoryAppsPageSchema = z.looseObject({
  content: z.array(supplyInventoryAppSchema),
  totalElements: z.number(),
  totalPages: z.number().optional(),
  number: z.number().optional(),
});

export const supplyAppsStatusCountsSchema = z.record(z.string(), z.number());

export const appRequestSchema = z
  .looseObject({
    id: nullableString,
    networkId: nullableOptional(identifierSchema),
    networkName: nullableString,
    publisherId: nullableOptional(identifierSchema),
    publisherSecondaryId: nullableString,
    publisherName: nullableString,
    platformId: nullableOptional(identifierSchema),
    platformName: nullableString,
    bundleId: nullableString,
    suggestedAppId: nullableOptional(identifierSchema),
    suggestedAppStoreId: nullableString,
    suggestedAppName: nullableString,
    connectedAppStoreId: nullableString,
    connectedAppName: nullableString,
    appStoreUrl: nullableString,
    adRequests: z.number().nullish(),
    appId: identifierSchema.nullable().optional(),
    requestAppName: nullableString,
    createdOn: nullableString,
    processedBy: nullableString,
    processedAt: nullableString,
    processedAy: nullableString,
    errorMsg: nullableString,
    processed: z.boolean().nullish(),
    blocked: z.boolean().nullish(),
    note: nullableString,
  })
  .transform((request) => ({
    ...request,
    requestKey:
      request.id ??
      [request.publisherId, request.bundleId, request.appStoreUrl, request.platformId]
        .filter(Boolean)
        .join('::'),
    adRequests: request.adRequests ?? 0,
    processed: request.processed ?? false,
    blocked: request.blocked ?? false,
    processedAt: request.processedAt ?? request.processedAy,
  }));

export const appRequestsPageSchema = z.looseObject({
  content: z.array(appRequestSchema),
  totalElements: z.number(),
  totalPages: z.number().optional(),
  number: z.number().optional(),
});

export const collectedAppDataSchema = z.looseObject({
  name: nullableString,
  appStoreUrl: nullableString,
  appStoreId: nullableString,
  bundleId: nullableString,
  bundleUrl: nullableString,
  domain: nullableString,
  adsTxtLocation: nullableString,
  description: nullableString,
  platform: supplyAppsReferenceSchema.nullish(),
  iabCategoriesIds: nullableArray(identifierSchema),
  iabCategoryIds: nullableArray(identifierSchema),
});

export const collectedAppDataResponseSchema = z
  .looseObject({ data: nullableArray(collectedAppDataSchema) })
  .transform((response) => response.data[0]);

export const publisherAppImportDraftSchema = z
  .looseObject({
    id: nullableOptional(identifierSchema),
    name: nullableString,
    appStoreUrl: nullableString,
    appStoreId: nullableString,
    bundleId: nullableString,
    bundleUrl: nullableString,
    domain: nullableString,
    adsTxtLocation: nullableString,
    description: nullableString,
    language: nullableString,
    platform: supplyAppsReferenceSchema.nullish(),
    uniqueApp: z.boolean().nullish(),
    invalidApp: z.boolean().nullish(),
    iabCategories: nullableArray(supplyAppsReferenceSchema),
    iabCategoriesIds: nullableArray(identifierSchema),
    iabCategoryIds: nullableArray(identifierSchema),
    adswizzGenresIds: nullableArray(identifierSchema),
  })
  .transform((app) => {
    let iabCategoriesIds = app.iabCategories.map((category) => category.id).filter(Boolean);
    if (app.iabCategoryIds.length) iabCategoriesIds = app.iabCategoryIds;
    if (app.iabCategoriesIds.length) iabCategoriesIds = app.iabCategoriesIds;
    return {
      ...app,
      rowId: app.id ?? crypto.randomUUID(),
      iabCategoriesIds,
    };
  });

export const publisherAppsImportResponseSchema = z.looseObject({
  data: z
    .array(publisherAppImportDraftSchema)
    .nullish()
    .transform((value) => value ?? []),
  message: nullableString,
  countInvalid: z.number().nullish(),
  countDuplicated: z.number().nullish(),
});

export enum PublisherAppsAddMethod {
  StoreUrls = 'URL',
  Manual = 'NON_URL',
  Csv = 'CSV',
}

export enum PublisherAppsAddStep {
  Method = 'METHOD',
  Input = 'INPUT',
  Preview = 'PREVIEW',
}

export enum PublisherAppImportStatus {
  Valid = 'VALID',
  Duplicate = 'DUPLICATE',
  Invalid = 'INVALID',
}

export type PublisherAppImportDraft = z.infer<typeof publisherAppImportDraftSchema>;

export interface PublisherAppImportPayload {
  publisherId: string;
  name: string | null;
  description: string | null;
  bundleId: string | null;
  bundleUrl: string | null;
  appStoreId: string | null;
  appStoreUrl: string | null;
  platform: string;
  domain: string | null;
  adsTxtLocation: string | null;
  uniqueApp: boolean;
  invalidApp: boolean;
  iabCategoriesIds: string[];
  adswizzGenresIds: string[];
  language: string | null;
}

export interface PublisherAppValidationPayload {
  appStoreUrl: string | null;
  name: string | null;
  appStoreId: string | null;
  platform: string;
  iabCategoriesIds: string[] | null;
  bundleId: string | null;
  domain: string | null;
}

export const appMutationResponseSchema = z.preprocess(
  (value) => (value === '' ? null : value),
  supplyInventoryAppSchema.nullable(),
);

export const createdPublisherAppSchema = z
  .looseObject({
    id: nullableOptional(identifierSchema),
    appId: nullableOptional(identifierSchema),
  })
  .transform((value, context) => {
    const id = value.id ?? value.appId;
    if (!id) {
      context.addIssue({ code: 'custom', message: 'Created app response is missing an id.' });
      return z.NEVER;
    }
    return { id };
  });

export const emptyMutationResponseSchema = z.preprocess(
  (value) => (value === '' ? null : value),
  z.union([z.null(), z.void(), z.looseObject({})]),
);

export interface SupplyAppsListFilter {
  searchTerm: string;
  statuses: SupplyInventoryAppStatus[];
  publisherIds: string[];
  platformIds: string[];
  iabCategoryIds: string[];
  page: number;
  size: number;
  sortProperty: SupplyAppsSortProperty | null;
  sortDirection: SupplyAppsSortDirection;
}

export interface AppRequestsListFilter {
  searchTerm: string;
  publisherIds: string[];
  platformIds: string[];
  processed: AppRequestBooleanFilter;
  blocked: AppRequestBooleanFilter;
  connectedWithApp: AppRequestBooleanFilter;
  bundleIds: string;
  appStoreUrls: string;
  datePreset: AppRequestDatePreset;
  page: number;
  size: number;
  sortProperty: AppRequestSortProperty | null;
  sortDirection: SupplyAppsSortDirection;
}

export interface SupplyAppMutationPayload {
  [key: string]: unknown;
  id: string;
  name: string;
  bundleId: string;
  appStoreUrl: string;
  domain: string | null;
  platform: Record<string, unknown>;
  appStoreId: string;
  numericId: string;
  language: string | null;
  publisherIds: string[];
  adsTxtLocation: string | null;
  iabCategories: string[];
  description: string | null;
}

export interface CreateAppFromRequestPayload {
  appStoreId: string;
  appStoreUrl: string;
  bundleId: string;
  bundleUrl: string | null;
  appId: string | null;
  name: string;
  platform: string;
  description: string;
  domain: string;
  adsTxtLocation: string;
  iabCategoriesIds: string[];
}

export type SupplyInventoryApp = z.infer<typeof supplyInventoryAppSchema>;
export type SupplyInventoryAppEdit = z.infer<typeof supplyInventoryAppEditSchema>;
export type SupplyAppsReference = z.infer<typeof supplyAppsReferenceSchema>;
export type AppRequest = z.infer<typeof appRequestSchema>;

export const supplyAppsPageSizes = [5, 10, 25, 50, 100] as const;

export const defaultSupplyAppsListFilter: SupplyAppsListFilter = {
  searchTerm: '',
  statuses: [SupplyInventoryAppStatus.Active],
  publisherIds: [],
  platformIds: [],
  iabCategoryIds: [],
  page: 0,
  size: 25,
  sortProperty: SupplyAppsSortProperty.CreatedOn,
  sortDirection: SupplyAppsSortDirection.Descending,
};

export const defaultAppRequestsListFilter: AppRequestsListFilter = {
  searchTerm: '',
  publisherIds: [],
  platformIds: [],
  processed: AppRequestBooleanFilter.All,
  blocked: AppRequestBooleanFilter.All,
  connectedWithApp: AppRequestBooleanFilter.All,
  bundleIds: '',
  appStoreUrls: '',
  datePreset: AppRequestDatePreset.Today,
  page: 0,
  size: 25,
  sortProperty: AppRequestSortProperty.CreatedOn,
  sortDirection: SupplyAppsSortDirection.Descending,
};

export function serializeSupplyAppsListFilter(filter: SupplyAppsListFilter) {
  return toSearchParams({
    searchTerm: filter.searchTerm,
    page: filter.page,
    size: filter.size,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
    statuses: filter.statuses,
    publisherIds: filter.publisherIds,
    platformIds: filter.platformIds,
    iabCategoriesIds: filter.iabCategoryIds,
  });
}

export function serializeAppRequestsListFilter(filter: AppRequestsListFilter, now = new Date()) {
  const dates = appRequestDateRange(filter.datePreset, now);
  return toSearchParams({
    searchTerm: filter.searchTerm,
    page: filter.page,
    size: filter.size,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
    publisherIds: filter.publisherIds,
    platformIds: filter.platformIds,
    processed: booleanFilterApiValue(filter.processed),
    blocked: booleanFilterApiValue(filter.blocked),
    connectedWithApp: booleanFilterApiValue(filter.connectedWithApp),
    bundleIds: filter.bundleIds,
    appStoreUrls: filter.appStoreUrls,
    dateFrom: dates.dateFrom,
    dateTo: dates.dateTo,
  });
}

export function appRequestDateRange(preset: AppRequestDatePreset, now: Date) {
  if (preset === AppRequestDatePreset.Today) return { dateFrom: '', dateTo: '' };
  const end = new Date(now);
  const start = new Date(now);
  start.setDate(start.getDate() - 6);
  return { dateFrom: localDate(start), dateTo: localDate(end) };
}

export function resolveSupplyAppStatusLabel(status: SupplyInventoryAppStatus) {
  return dashboardT(
    status === SupplyInventoryAppStatus.Active
      ? 'supplyApps.status.active'
      : 'supplyApps.status.archived',
  );
}

export function resolveSupplyAppStatusTone(status: SupplyInventoryAppStatus) {
  return status === SupplyInventoryAppStatus.Active
    ? DashboardTableStatusTone.Success
    : DashboardTableStatusTone.Neutral;
}

export function isSupplyAppsSortProperty(value: string): value is SupplyAppsSortProperty {
  return new Set<string>(Object.values(SupplyAppsSortProperty)).has(value);
}

export function isAppRequestSortProperty(value: string): value is AppRequestSortProperty {
  return new Set<string>(Object.values(AppRequestSortProperty)).has(value);
}

function booleanFilterApiValue(value: AppRequestBooleanFilter) {
  if (value === AppRequestBooleanFilter.All) return '';
  return value === AppRequestBooleanFilter.Yes;
}

function localDate(value: Date) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${String(year)}-${month}-${day}`;
}
